import React, {useEffect, useState, memo} from 'react';
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { useTranslation } from 'react-i18next';
import { toast } from '../app';
import { ipcRenderer } from '../electron-modules';
import { Viewer } from '../viewer/js/main';
import { DraggableDialog, EnhancedTable, PromptDialog, TextDialog } from '../components';
import { Button, Checkbox, DialogActions, DialogContent, Typography } from '@material-ui/core';
import { LocalObservation, LocalObservationTypes } from '../viewer/js/data-aligner';
import { AerialObservationIcon, ObservationError, PointObservationIcon } from './observations';
import { changeDataProjection, changeViewProjection, selectDataProjection, Transform } from '../redux/projections-slice';
import { useDispatch, useSelector } from 'react-redux';
import { projectionToUnits } from '../viewer/js/projections';
import { UnitConverter } from '../viewer/js/utilities';
import { userProjections } from '../projections';
import { trackToolUsage } from '../executable';

const highCutoff = 0.5;
const lowCutoff = 0.25;
const requiredRows = 3;

const converter = new UnitConverter();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    marginTop: {
      marginTop: theme.spacing(2)
    },
    minHeightContent: {
      minHeight: "100px"
    },
    transformInfo: {
      display: "flex",
      justifyContent: "space-evenly",
      paddingTop: theme.spacing(1)
    },
    toggleOptionFlex: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    toggleOptionParent: {
      margin: theme.spacing(0),
      padding: theme.spacing(0),
      boxShadow: "0px 10px 10px 2px",
      justifyContent: "center"
    },
    toggleOptionCheckbox: {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5)
    },
    transformValues: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center"
    }
  }),
);

const AlignmentError = (props) => {
  const lowCutoffWithUnits = converter.convert(lowCutoff, "m", props.units);
  const highCutoffWithUnits = converter.convert(highCutoff, "m", props.units);

  return <ObservationError
    value={props.value}
    lowCutoff={lowCutoffWithUnits}
    highCutoff={highCutoffWithUnits}
  />;
};

export const LocalAligner = memo((props: any) => {
  const viewer = props.viewer as Viewer;
  const observations = props.observations as LocalObservation[];
  const {open, setOpen, canOpenTool} = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const dataProjection = useSelector(selectDataProjection);
  const units = projectionToUnits(dataProjection);

  const [calculating, setCalculating] = useState(false);
  const [rotate, setRotate] = useState(true);
  const [scale, setScale] = useState(true);
  const [closePrompt, setClosePrompt] = useState(false);
  const [utmZone, setUtmZone] = useState(0);
  const [savePrompt, setSavePrompt] = useState(false);
  const [oldProjection, setOldProjection] = useState(null);
  const [newProjection, setNewProjection] = useState(null);

  const handleOpen = () => {
    if (!canOpenTool) return toast.warning(t('toast.one_tool_only'));

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleScale = () => {
    setScale(!scale);
  };

  const handleRotate = () => {
    setRotate(!rotate);
  };

  const addNewRow = () => {
    viewer.generateLocalAlignerRows(1);
    if (!calculated) return;
    onCancel();
  };

  const onDeleteClick = (row) => {
    viewer.deleteLocalAlignment(row.id);
    setNewProjection(null);
    if (!calculated) return;
    onCancel();
  };

  const checkClose = () => {
    if (!isTableEmpty()) {
      setClosePrompt(true);
    } else {
      handleClose();
    }
  };

  const isTableEmpty = () => {
    const filledRows = observations.filter(observation => {
      return observation.scene || observation.aerial;
    });

    return filledRows.length === 0;
  };

  const isTableFilled = () => {
    const filledRows = observations.filter(observation => {
      return observation.scene && observation.aerial;
    });

    return filledRows.length === observations.length;
  };

  const getEstimator = () => {
    let estimator = "T";
    if (scale) estimator += "S";
    if (rotate) estimator += "R";
    return estimator;
  };

  const tempProjectionName = () => {
    const randomString = (Math.random() + 1).toString(36);
    return `Temp Projection ${randomString.substring(4)}`;
  };

  const runCalculation = async () => {
    setCalculating(true);
    trackToolUsage("aligner-local");

    const estimator = getEstimator();
    const {projection, utmZone} =
      await viewer.estimateLocalTransform(estimator, units);

    const tempProjection = {
      ...projection,
      name: tempProjectionName()
    };

    userProjections.save(tempProjection);
    dispatch(changeDataProjection(tempProjection));
    dispatch(changeViewProjection(tempProjection));

    setNewProjection(tempProjection);
    setUtmZone(utmZone);

    setCalculating(false);
    toast.success(t('local-projection.calculation_completed'));
  };

  const onCancel = () => {
    // Revert back to original projection
    updateViewerProjection(oldProjection);

    // Delete new projection
    deleteViewerProjection(newProjection);

    // Clear calculated values
    viewer.clearLocalTransformErrors();
    setNewProjection(null);
  };

  const saveNewProjection = () => {
    setSavePrompt(true);
  };

  const deleteViewerProjection = (projection) => {
    if (!projection) return;
    userProjections.delete(projection.name);
  };

  const updateViewerProjection = (projection) => {
    if (dataProjection === projection) return;
    dispatch(changeDataProjection(projection));
    dispatch(changeViewProjection(projection));
  };

  const onAddProjection = (name) => {
    if (userProjections.exists(name)) {
      toast.error(t("toast.projection-duplicate"));
      return;
    }

    const projection = {...newProjection, name};
    userProjections.save(projection);
    updateViewerProjection(projection);
    setSavePrompt(false);
    setOpen(false);
  };

  const resetDefaults = () => {
    setRotate(true);
    setScale(true);
    setNewProjection(null);
  };

  const onAerialClick = (id) => {
    viewer.setLocalAlignerActive(id, LocalObservationTypes.Aerial);
    if (!calculated) return;
    onCancel();
  };

  const onSceneClick = (id) => {
    viewer.setLocalAlignerActive(id, LocalObservationTypes.Scene);
    if (!calculated) return;
    onCancel();
  };

  const getTransformValues = () => {
    let translateX;
    let translateY;
    let rotation;
    let scaling;

    if (calculated) {
      const transform = newProjection.transform as Transform;
      translateX = transform.origin[0].toFixed(2);
      translateY = transform.origin[1].toFixed(2);
      rotation = (-1*transform.rotation).toFixed(2);
      scaling = (1.0/transform.scale).toFixed(2);
    }

    return {translateX, translateY, rotation, scaling};
  };

  useEffect(() => {
    viewer?.toggleLocalAligner(open);

    if (open) {
      viewer?.generateLocalAlignerRows(requiredRows);
      setOldProjection(dataProjection);
    } else {
      resetDefaults();
    }
  }, [viewer, open]);

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-local-projection");
    ipcRenderer?.on("open-local-projection", handleOpen);
  }, [open, canOpenTool]);

  const calculated = !!newProjection;
  const {translateX, translateY, rotation, scaling}
    = getTransformValues();

  const canSubmit = isTableFilled()
    && observations.length >= requiredRows
    && !calculating;

  const columns = [
    {
      id: "obs_num",
      label: '#',
      numeric: true
    },
    {
      id: "map_obs",
      label: t('local-projection.aerial_control'),
      center: true
    },
    {
      id: "pc_obs",
      label: t('local-projection.point_control'),
      center: true
    },
    {
      id: "error",
      label: t('local-projection.error_units', {units}),
      numeric: true,
      forceOpacity: true
    }
  ];

  const rows = observations.map((observation, index) => {
    const disabled = calculating;

    const aerialSelected = !!observation.aerial;
    const aerialClicked = observation.active === LocalObservationTypes.Aerial;

    const pointSelected = !!observation.scene;
    const pointClicked = observation.active === LocalObservationTypes.Scene;

    const aerialObs = (
      <AerialObservationIcon
        selected={aerialSelected}
        clicked={aerialClicked}
        title={t('observation.add_aerial_observation')}
        onClick={() => {
          if (disabled) return;
          onAerialClick(observation.id);
        }}
      />
    );

    const pointObs = (
      <PointObservationIcon
        selected={pointSelected}
        clicked={pointClicked}
        title={t('observation.add_point_observation')}
        onClick={() => {
          if (disabled) return;
          onSceneClick(observation.id);
        }}
      />
    );

    const error = (
      <AlignmentError
        value={observation.error}
        units={units}
      />
    );

    return {
      id: observation.id,
      obs_num: index+1,
      map_obs: aerialObs,
      pc_obs: pointObs,
      error: error,
      disabled
    };
  });

  return (
    <React.Fragment>

      <DraggableDialog
        open={open}
        title={t('local-projection.local_projection_aligner')}
        onClose={checkClose}
      >
        <DialogContent className={classes.minHeightContent}>
          <Typography variant="caption">
            {t("local-projection.select-at-least", {requiredRows})}
          </Typography>

          <EnhancedTable
            dense
            deletable
            sortable={false}
            columns={columns}
            rows={rows}
            className={classes.marginTop}
            rowType={t('local-projection.control_point')}
            onDeleteClick={onDeleteClick}
          />
        </DialogContent>

        {calculated && (<React.Fragment>
          <div className={classes.transformInfo}>
            <Typography variant="caption" className={classes.transformValues}>
              <b>{t('local-projection.estimated_rotation')} </b> {rotation}
            </Typography>

            <Typography variant="caption" className={classes.transformValues}>
              <b>{t('local-projection.estimated_scale')} </b> {scaling}
            </Typography>
          </div>

          <div className={classes.transformInfo}>
            <Typography variant="caption" className={classes.transformValues}>
              <b>{t('local-projection.estimated_offset')} </b> [{translateX}, {translateY}]
            </Typography>

            <Typography variant="caption" className={classes.transformValues}>
              <b>{t('local-projection.closest_utm_zone')} </b> {utmZone}
            </Typography>
          </div>
        </React.Fragment>)}

        <DialogActions>
          {/** Add new observation row */}
          <Button disabled={calculating} onClick={addNewRow}>
            {t('local-projection.add_entry')}
          </Button>

          {/** Cancel projection */}
          {calculated && (<React.Fragment>
            <Button onClick={onCancel} color="secondary">
              {t('local-projection.clear_local_projection')}
            </Button>
          </React.Fragment>)}

          {/** Run python calculations */}
          {!calculated && (<React.Fragment>
            <Button disabled={!canSubmit} onClick={runCalculation}>
              {t('local-projection.calculate_transform')}
            </Button>
          </React.Fragment>)}

          {/** Confirm projection */}
          {calculated && (<React.Fragment>
            <Button onClick={saveNewProjection}>
              {t('local-projection.save_local_projection')}
            </Button>
          </React.Fragment>)}
        </DialogActions>

        <DialogActions className={classes.toggleOptionParent}>
          {/* Allow scaling while estimating transform */}
          <div className={classes.toggleOptionFlex}>
            <Typography variant="caption">
              {t('local-projection.allow_scaling')}
            </Typography>
            <Checkbox
              disabled={calculating}
              checked={scale}
              onChange={handleScale}
              classes={{root: classes.toggleOptionCheckbox}}
              size="small"
            />
          </div>

          {/* Allow rotation while estimating transform */}
          <div className={classes.toggleOptionFlex}>
            <Typography variant="caption">
              {t('local-projection.allow_rotation')}
            </Typography>
            <Checkbox
              disabled={calculating}
              checked={rotate}
              onChange={handleRotate}
              classes={{root: classes.toggleOptionCheckbox}}
              size="small"
            />
          </div>
        </DialogActions>

      </DraggableDialog>

      <TextDialog
        open={savePrompt}
        onCancel={() => setSavePrompt(false)}
        onSubmit={onAddProjection}
        title={t('local-projection.create_new_projection')}
        prompt={t('local-projection.enter_local_projection_name')}
        default=''
        label={t('local-projection.projection_name')}
        clear={true}
      />

      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          onCancel();
          handleClose();
          setClosePrompt(false);
        }}
        title={t('local-projection.close_local_alignment')}
        prompt={t('local-projection.do_you_want_to_close_the_local_projection_aligner')}
        button={t('buttons.close')}
      />

    </React.Fragment>
  );
});
